import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, IndianRupee, ShoppingBag, Users, RefreshCw,
  CircleCheck, XCircle, Plus, Pencil, Bell, Volume2, VolumeX,
  Phone, MapPin, StickyNote, CreditCard, Clock, KeyRound, Trash2, AlertTriangle,
  Upload, Image as ImageIcon,
} from "lucide-react";
import { api } from "../lib/api";

const STATUS_FLOW = [
  "Order Confirmed",
  "Preparing Food",
  "Out for Delivery",
  "Delivered",
];

const ACTIVE_STATUSES = new Set([
  "Order Confirmed", "Preparing Food", "Ready for Pickup",
  "Picked Up by Rider", "On the Way", "Out for Delivery",
]);

// Normalize historic statuses to the simplified delivery-only flow for display
function normalizeStatus(s) {
  if (s === "Picked Up by Rider" || s === "On the Way") return "Out for Delivery";
  if (s === "Ready for Pickup") return "Preparing Food";
  return s;
}

// Persistent singleton AudioContext — must be created / resumed via user gesture
let _audioCtx = null;
let _audioUnlocked = false;

function getAudioCtx() {
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch { return null; }
  }
  return _audioCtx;
}

async function unlockAudio() {
  const ctx = getAudioCtx();
  if (!ctx) return false;
  try {
    if (ctx.state === "suspended") await ctx.resume();
    // Play a silent 1ms tone to fully unlock the context on iOS/Safari
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.value = 0.0001;
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.01);
    _audioUnlocked = true;
    return true;
  } catch {
    return false;
  }
}

function beep() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine"; o.frequency.value = 880;
    o.connect(g); g.connect(ctx.destination);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    o.start(); o.stop(ctx.currentTime + 0.5);
  } catch {}
}

// Loud siren-like alert that LOOPS until explicitly stopped.
function startSiren() {
  const ctx = getAudioCtx();
  if (!ctx) return () => {};
  // If context got suspended (tab was hidden), try to resume
  if (ctx.state === "suspended") { try { ctx.resume(); } catch {} }

  let stopped = false;
  const master = ctx.createGain();
  master.gain.value = 1.0; // loud
  master.connect(ctx.destination);

  const playCycle = (startAt) => {
    if (stopped) return;
    const pattern = [
      { f: 1100, start: 0.00, dur: 0.22 },
      { f: 850,  start: 0.24, dur: 0.22 },
      { f: 1100, start: 0.48, dur: 0.22 },
      { f: 850,  start: 0.72, dur: 0.22 },
      { f: 1400, start: 0.96, dur: 0.40 },
    ];
    pattern.forEach(({ f, start, dur }) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.setValueAtTime(f, startAt + start);
      o.connect(g); g.connect(master);
      g.gain.setValueAtTime(0.0001, startAt + start);
      g.gain.exponentialRampToValueAtTime(0.7, startAt + start + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, startAt + start + dur);
      o.start(startAt + start);
      o.stop(startAt + start + dur + 0.02);
    });
  };

  const cycleMs = 1600;
  playCycle(ctx.currentTime);
  const iv = setInterval(() => {
    if (stopped) return;
    playCycle(ctx.currentTime);
  }, cycleMs);

  return () => {
    stopped = true;
    clearInterval(iv);
    try { master.disconnect(); } catch {}
  };
}

function vibrate() {
  try {
    if (navigator.vibrate) navigator.vibrate([400, 150, 400, 150, 400, 150, 800]);
  } catch {}
}

function flashTitle(text) {
  if (!document) return () => {};
  const original = document.title;
  let on = true;
  const iv = setInterval(() => {
    document.title = on ? text : original;
    on = !on;
  }, 700);
  return () => { clearInterval(iv); document.title = original; };
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("mj_admin_sound") !== "off");
  const [audioReady, setAudioReady] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [incoming, setIncoming] = useState([]); // new orders pending admin acknowledgment
  const [users, setUsers] = useState([]);
  const [storeOpen, setStoreOpen] = useState(true);
  const [storeUpdating, setStoreUpdating] = useState(false);
  const prevOrderIdsRef = useRef(null);
  const sirenStopRef = useRef(null);
  const titleStopRef = useRef(null);
  const navigate = useNavigate();

  const stopAlert = () => {
    if (sirenStopRef.current) { sirenStopRef.current(); sirenStopRef.current = null; }
    if (titleStopRef.current) { titleStopRef.current(); titleStopRef.current = null; }
    setIncoming([]);
  };

  // Unlock audio on the very first interaction anywhere on the page
  useEffect(() => {
    const tryUnlock = async () => {
      const ok = await unlockAudio();
      if (ok) {
        setAudioReady(true);
        window.removeEventListener("click", tryUnlock);
        window.removeEventListener("keydown", tryUnlock);
        window.removeEventListener("touchstart", tryUnlock);
      }
    };
    window.addEventListener("click", tryUnlock);
    window.addEventListener("keydown", tryUnlock);
    window.addEventListener("touchstart", tryUnlock);
    return () => {
      window.removeEventListener("click", tryUnlock);
      window.removeEventListener("keydown", tryUnlock);
      window.removeEventListener("touchstart", tryUnlock);
    };
  }, []);

  const loadAll = async () => {
    try {
      const [o, m, a, u] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/menu"),
        api.get("/admin/analytics"),
        api.get("/admin/users"),
      ]);
      // Detect new orders since last poll for sound/toast alerts
      const newIds = new Set(o.data.map((x) => x.id));
      if (prevOrderIdsRef.current) {
        const fresh = o.data.filter((x) => !prevOrderIdsRef.current.has(x.id));
        if (fresh.length > 0) {
          // Queue them into the acknowledgment modal
          setIncoming((cur) => [...fresh, ...cur]);
          fresh.forEach((x) => toast.success(`NEW ORDER · #${x.id.slice(0, 8).toUpperCase()} · ₹${x.total}`));
          if (soundOn) {
            // Stop any existing siren then start a fresh one that loops until ack
            if (sirenStopRef.current) sirenStopRef.current();
            sirenStopRef.current = startSiren();
            vibrate();
          }
          if (titleStopRef.current) titleStopRef.current();
          titleStopRef.current = flashTitle(`🔔 ${fresh.length} NEW ORDER${fresh.length > 1 ? "S" : ""}`);
        }
      }
      prevOrderIdsRef.current = newIds;

      setOrders(o.data); setMenu(m.data); setAnalytics(a.data); setUsers(u.data);
      // Pull current store status (public endpoint, no auth needed)
      try {
        const s = await api.get("/store-status");
        setStoreOpen(!!s.data.is_open);
      } catch {}
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("mj_admin_token");
        navigate("/admin/login");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("mj_admin_token")) { navigate("/admin/login"); return; }
    loadAll();
    const t = setInterval(loadAll, 6000);
    return () => {
      clearInterval(t);
      if (sirenStopRef.current) sirenStopRef.current();
      if (titleStopRef.current) titleStopRef.current();
    };
    // eslint-disable-next-line
  }, [soundOn]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("mj_admin_sound", next ? "on" : "off");
    if (!next) { if (sirenStopRef.current) { sirenStopRef.current(); sirenStopRef.current = null; } }
    else { beep(); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status });
    toast.success(`Status: ${status}`);
    loadAll();
  };

  const deleteOrder = async (id) => {
    if (!confirm(`Permanently delete order #${id.slice(0, 8).toUpperCase()}? This cannot be undone.`)) return;
    await api.delete(`/admin/orders/${id}`);
    toast.success("Order deleted");
    loadAll();
  };

  const resetAnalytics = async () => {
    await api.delete(`/admin/orders`, { params: { confirm: "RESET_ANALYTICS" } });
    toast.success("All orders wiped · analytics reset");
    loadAll();
  };

  const newOrdersCount = useMemo(
    () => orders.filter((o) => o.status === "Order Confirmed").length,
    [orders]
  );

  const toggleStoreStatus = async () => {
    if (storeUpdating) return;
    const next = !storeOpen;
    if (!next) {
      const ok = window.confirm("Close the store?\n\nCustomers will see a 'Sorry, we are closed today!' banner and won't be able to place orders.");
      if (!ok) return;
    }
    setStoreUpdating(true);
    try {
      const { data } = await api.post("/admin/store-status", { is_open: next });
      setStoreOpen(!!data.is_open);
      toast.success(data.is_open ? "Store is now OPEN — accepting orders" : "Store is now CLOSED — orders blocked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update store status");
    } finally {
      setStoreUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F7]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-black" />
            <div>
              <div className="font-display font-black leading-tight">Mok.Jaa</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Admin Console</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleSound} className="rounded-full p-2 hover:bg-gray-100" data-testid="admin-sound-toggle" title={soundOn ? "Sound on" : "Sound muted"}>
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-gray-400" />}
            </button>
            <button
              onClick={() => { if (!audioReady) { unlockAudio().then(() => setAudioReady(true)); } beep(); toast.success("🔔 Test sound"); }}
              className="hidden md:inline-flex rounded-full px-3 py-1.5 text-xs font-bold border border-gray-200 hover:border-black"
              data-testid="admin-test-sound"
            >
              Test sound
            </button>
            <button onClick={loadAll} className="rounded-full p-2 hover:bg-gray-100" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={() => setShowPwd(true)} className="rounded-full p-2 hover:bg-gray-100" data-testid="admin-change-password-btn" title="Change password">
              <KeyRound className="w-4 h-4" />
            </button>
            <button onClick={() => { localStorage.removeItem("mj_admin_token"); localStorage.removeItem("mj_admin_expires_at"); navigate("/admin/login"); }} className="rounded-full p-2 hover:bg-gray-100" data-testid="admin-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto hide-scrollbar">
          {[
            { key: "orders", label: "Orders", badge: newOrdersCount },
            { key: "menu", label: "Menu" },
            { key: "users", label: "Users" },
            { key: "analytics", label: "Analytics" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`py-3 px-4 text-sm font-bold capitalize border-b-2 relative ${tab === t.key ? "border-black text-black" : "border-transparent text-gray-500"}`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
              {t.badge > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#C85A5A] text-white text-[10px] font-black mj-pulse" data-testid={`badge-${t.key}`}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Store status toggle */}
        <div
          className={`mb-4 rounded-2xl p-4 md:p-5 flex items-center justify-between gap-4 border-2 ${storeOpen ? "border-green-500 bg-green-50" : "border-[#C85A5A] bg-red-50"}`}
          data-testid="store-status-card"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-3 h-3 rounded-full shrink-0 ${storeOpen ? "bg-green-500 mj-pulse" : "bg-[#C85A5A]"}`} />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Store Status</div>
              <div className="font-display font-black text-xl md:text-2xl mt-0.5" data-testid="store-status-label">
                {storeOpen ? "OPEN" : "CLOSED"}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">
                {storeOpen
                  ? "Customers can browse the menu and place orders."
                  : "Customers see a 'Sorry, we are closed today!' banner. Orders are blocked."}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleStoreStatus}
            disabled={storeUpdating}
            role="switch"
            aria-checked={storeOpen}
            aria-label="Toggle store status"
            data-testid="store-status-toggle"
            className={`relative shrink-0 inline-flex items-center h-10 w-20 rounded-full transition-colors duration-200 disabled:opacity-60 ${storeOpen ? "bg-black" : "bg-gray-300"}`}
          >
            <span
              className={`inline-block w-8 h-8 rounded-full bg-white shadow transform transition-transform duration-200 ${storeOpen ? "translate-x-11" : "translate-x-1"}`}
            />
            <span className={`absolute text-[10px] font-black uppercase tracking-widest ${storeOpen ? "left-2 text-white" : "right-2 text-gray-600"}`}>
              {storeOpen ? "On" : "Off"}
            </span>
          </button>
        </div>

        {!audioReady && soundOn && (
          <div className="mb-4 rounded-2xl border-2 border-[#C85A5A] bg-red-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="audio-unlock-banner">
            <div className="flex items-center gap-2 text-sm">
              <Bell className="w-4 h-4 text-[#C85A5A]" />
              <span className="text-gray-800"><b>Browser is blocking the siren.</b> Click anywhere (or the button) to enable new-order alerts.</span>
            </div>
            <button
              onClick={async () => { const ok = await unlockAudio(); if (ok) { setAudioReady(true); beep(); toast.success("Sound enabled"); } }}
              className="shrink-0 bg-black text-white rounded-full px-4 py-1.5 text-xs font-bold"
              data-testid="enable-audio-btn"
            >
              Enable sound
            </button>
          </div>
        )}
        {tab === "orders" && <OrdersPanel orders={orders} onStatus={setStatus} onDelete={deleteOrder} />}
        {tab === "menu" && <MenuPanel menu={menu} onRefresh={loadAll} />}
        {tab === "users" && <UsersPanel users={users} onRefresh={loadAll} />}
        {tab === "analytics" && <AnalyticsPanel analytics={analytics} onReset={resetAnalytics} />}
      </main>

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
      {incoming.length > 0 && <IncomingOrderOverlay orders={incoming} onAck={stopAlert} />}
    </div>
  );
}

function IncomingOrderOverlay({ orders, onAck }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="incoming-order-overlay">
      <div className="absolute inset-0 bg-[#C85A5A]/20 animate-pulse pointer-events-none" />
      <div className="relative bg-white rounded-3xl w-full max-w-lg p-6 md:p-8 shadow-[0_32px_80px_rgba(0,0,0,0.5)]">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-14 h-14 rounded-full bg-[#C85A5A] text-white grid place-items-center mj-pulse shrink-0">
            <Bell className="w-7 h-7" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C85A5A]">Incoming</div>
            <div className="font-display font-black text-3xl md:text-4xl tracking-tighter">
              {orders.length} new order{orders.length > 1 ? "s" : ""}
            </div>
          </div>
        </div>

        <div className="space-y-2 max-h-[40vh] overflow-y-auto border-y border-gray-100 py-4">
          {orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-3 bg-[#F7F7F7] rounded-xl p-3" data-testid={`incoming-${o.id}`}>
              <div>
                <div className="font-display font-black text-base">#{o.id.slice(0, 8).toUpperCase()}</div>
                <div className="text-xs text-gray-500">{o.user_name || o.user_email} · {o.items.length} item{o.items.length > 1 ? "s" : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-black">₹{o.total}</div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">{o.payment_method}</div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={onAck}
          className="mt-5 w-full bg-black text-white rounded-full py-4 font-black text-lg hover:bg-gray-800 active:scale-[0.99] transition"
          data-testid="ack-incoming-btn"
          autoFocus
        >
          Confirm order and deliver!
        </button>
        <p className="text-xs text-gray-500 text-center mt-3">Siren keeps playing until you acknowledge.</p>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    if (newPw.length < 10) { toast.error("New password must be at least 10 characters"); return; }
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated. Please sign in again.");
      localStorage.removeItem("mj_admin_token");
      localStorage.removeItem("mj_admin_expires_at");
      window.location.href = "/admin/login";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="change-password-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-1">Security</div>
        <div className="font-display font-black text-2xl mb-1">Change password</div>
        <p className="text-sm text-gray-500 mb-5">This is the only way to rotate the admin password.</p>

        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">Current password</div>
            <input type="password" className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-black" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="cur-pw" autoComplete="current-password" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">New password <span className="text-gray-400 normal-case tracking-normal">(min 10 chars)</span></div>
            <input type="password" className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-black" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-pw" autoComplete="new-password" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">Confirm new password</div>
            <input type="password" className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-black" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-pw" autoComplete="new-password" />
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-bold text-gray-500" data-testid="pw-cancel">Cancel</button>
          <button onClick={save} disabled={saving || !curPw || !newPw || !confirmPw} className="bg-black text-white rounded-full px-5 py-2 text-sm font-bold disabled:bg-gray-400" data-testid="pw-save">
            {saving ? "Updating..." : "Update password"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- ORDERS ----------------- */
function OrdersPanel({ orders, onStatus, onDelete }) {
  const [filter, setFilter] = useState("active");

  const filtered = useMemo(() => {
    if (filter === "active") return orders.filter((o) => ACTIVE_STATUSES.has(o.status));
    if (filter === "new") return orders.filter((o) => o.status === "Order Confirmed");
    if (filter === "delivered") return orders.filter((o) => o.status === "Delivered");
    if (filter === "cancelled") return orders.filter((o) => o.status === "Cancelled");
    return orders;
  }, [orders, filter]);

  const stats = useMemo(() => ({
    new: orders.filter((o) => o.status === "Order Confirmed").length,
    active: orders.filter((o) => ACTIVE_STATUSES.has(o.status)).length,
    done: orders.filter((o) => o.status === "Delivered").length,
    rev: orders.filter((o) => o.status !== "Cancelled").reduce((s, o) => s + o.total, 0),
  }), [orders]);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard icon={Bell} label="New" value={stats.new} accent={stats.new > 0} />
        <StatCard icon={ShoppingBag} label="Active" value={stats.active} />
        <StatCard icon={CircleCheck} label="Delivered" value={stats.done} />
        <StatCard icon={IndianRupee} label="Revenue" value={`₹${stats.rev}`} />
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar">
        {[
          { k: "active", l: "Active" },
          { k: "new", l: "New" },
          { k: "delivered", l: "Delivered" },
          { k: "cancelled", l: "Cancelled" },
          { k: "all", l: "All" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-bold border ${filter === f.k ? "bg-black text-white border-black" : "bg-white border-gray-200 text-gray-600"}`}
            data-testid={`order-filter-${f.k}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">No orders here.</div>}
        {filtered.map((o) => (
          <OrderCard key={o.id} order={o} onStatus={onStatus} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function OrderCard({ order, onStatus, onDelete }) {
  const isNew = order.status === "Order Confirmed";
  const minsAgo = Math.max(0, Math.floor((Date.now() - new Date(order.created_at).getTime()) / 60000));
  const disp = normalizeStatus(order.status);

  const currentIdx = STATUS_FLOW.indexOf(disp);
  const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentIdx + 1] : null;

  return (
    <div className={`bg-white rounded-2xl border p-5 ${isNew ? "border-[#C85A5A]/40 ring-1 ring-[#C85A5A]/20" : "border-gray-100"}`} data-testid={`admin-order-${order.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-black text-lg">#{order.id.slice(0, 8).toUpperCase()}</div>
            {isNew && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#C85A5A] text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5 mj-pulse" data-testid={`new-badge-${order.id}`}>
                <Bell className="w-3 h-3" /> New
              </span>
            )}
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-bold">{disp}</span>
          </div>
          <div className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {minsAgo < 60 ? `${minsAgo} min ago` : new Date(order.created_at).toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-xl">₹{order.total}</div>
          <div className="inline-flex items-center gap-1 text-xs text-gray-500 mt-0.5">
            <CreditCard className="w-3 h-3" /> {order.payment_method} · {order.payment_status}
          </div>
        </div>
      </div>

      {/* Customer */}
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Customer</div>
          <div className="font-bold">{order.user_name || order.user_email}</div>
          <div className="text-gray-500 text-xs">{order.user_email}</div>
          <a href={`tel:${order.address?.phone}`} className="inline-flex items-center gap-1 text-xs text-black font-bold mt-1 hover:underline" data-testid={`phone-${order.id}`}>
            <Phone className="w-3 h-3" /> {order.address?.phone || "—"}
          </a>
        </div>
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Delivery address</div>
          <div className="text-gray-700">{order.address?.label} · {order.address?.line1}{order.address?.line2 ? `, ${order.address.line2}` : ""}</div>
          <div className="text-gray-500 text-xs">{order.address?.city} – {order.address?.pincode}</div>
        </div>
      </div>

      {/* Items */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Items ({order.items.length})</div>
        <ul className="mt-1 text-sm space-y-0.5">
          {order.items.map((i, idx) => (
            <li key={idx} className="flex justify-between">
              <span>{i.qty} × {i.name}{i.variant ? <span className="text-gray-400"> ({i.variant})</span> : null}</span>
              <span className="text-gray-500">₹{i.price * i.qty}</span>
            </li>
          ))}
        </ul>
      </div>

      {order.notes && (
        <div className="mt-3 rounded-xl bg-[#FFF8E1] border border-amber-100 p-3 text-sm flex items-start gap-2">
          <StickyNote className="w-4 h-4 mt-0.5 text-amber-700" />
          <div><div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Note from customer</div>{order.notes}</div>
        </div>
      )}

      {/* Controls */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {STATUS_FLOW.map((s, i) => {
          const isDone = i < currentIdx;
          const isCurrent = i === currentIdx;
          const isNext = i === currentIdx + 1;
          return (
            <button
              key={s}
              onClick={() => onStatus(order.id, s)}
              disabled={order.status === s || order.status === "Cancelled" || order.status === "Delivered"}
              className={`text-xs font-bold rounded-full px-3.5 py-1.5 border transition
                ${isCurrent ? "bg-black text-white border-black" : ""}
                ${isDone ? "bg-gray-100 text-gray-400 border-gray-100" : ""}
                ${isNext ? "bg-white text-black border-black hover:bg-black hover:text-white" : ""}
                ${!isCurrent && !isDone && !isNext ? "bg-white text-gray-400 border-gray-200 hover:border-black hover:text-black" : ""}
                disabled:opacity-60 disabled:cursor-not-allowed`}
              data-testid={`btn-status-${order.id}-${s.replace(/\s+/g, '-')}`}
            >
              {s}
            </button>
          );
        })}
        <button
          onClick={() => { if (confirm("Cancel this order?")) onStatus(order.id, "Cancelled"); }}
          disabled={order.status === "Cancelled" || order.status === "Delivered"}
          className="text-xs font-bold rounded-full px-3.5 py-1.5 border border-[#C85A5A] text-[#C85A5A] hover:bg-[#C85A5A] hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`btn-cancel-${order.id}`}
        >
          Cancel
        </button>

        <button
          onClick={() => onDelete(order.id)}
          className="text-xs font-bold rounded-full px-3 py-1.5 border border-gray-200 text-gray-500 hover:border-[#C85A5A] hover:text-[#C85A5A] hover:bg-red-50 transition inline-flex items-center gap-1"
          data-testid={`btn-delete-${order.id}`}
          title="Permanently delete this order"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}

/* ----------------- MENU ----------------- */
function MenuPanel({ menu, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const grouped = useMemo(() => {
    const m = {};
    menu.forEach((p) => { (m[p.category] ||= []).push(p); });
    return m;
  }, [menu]);

  const toggle = async (p) => {
    await api.patch(`/admin/menu/${p.id}/availability`, null, { params: { available: !p.available } });
    onRefresh();
  };
  const del = async (p) => {
    if (!confirm(`Remove "${p.name}" permanently?`)) return;
    await api.delete(`/admin/menu/${p.id}`);
    toast.success("Removed");
    onRefresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="font-display font-black text-2xl">Menu</div>
          <div className="text-sm text-gray-500">Add, edit, price & availability. Unavailable items are hidden from customers.</div>
        </div>
        <button onClick={() => setCreating(true)} className="bg-black text-white rounded-full px-5 py-2 text-sm font-bold inline-flex items-center gap-1.5" data-testid="menu-add-btn">
          <Plus className="w-4 h-4" /> Add item
        </button>
      </div>

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="bg-white rounded-2xl border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100 font-display font-bold">{cat}</div>
          <div className="divide-y divide-gray-100">
            {items.map((p) => (
              <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`admin-menu-${p.id}`}>
                <img src={p.image} alt={p.name} className="w-14 h-14 rounded-xl object-cover bg-gray-100" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{p.name} {p.popular && <span className="text-[9px] uppercase tracking-widest ml-2 bg-black text-white px-1.5 py-0.5 rounded-full">Popular</span>} {p.spicy && <span className="text-[9px] ml-1 bg-[#C85A5A] text-white px-1.5 py-0.5 rounded-full">Spicy</span>}</div>
                  <div className="text-xs text-gray-500 truncate">₹{p.price} · {p.variants?.length ? `${p.variants.length} variants` : "no variants"}</div>
                </div>
                <button onClick={() => toggle(p)} className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border-2 ${p.available ? "border-black text-black" : "border-gray-200 text-gray-400"}`} data-testid={`toggle-${p.id}`}>
                  {p.available ? <CircleCheck className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                  {p.available ? "Available" : "Unavailable"}
                </button>
                <button onClick={() => setEditing(p)} className="rounded-full p-2 hover:bg-gray-100" data-testid={`edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p)} className="rounded-full p-2 hover:bg-gray-100 text-[#C85A5A]" data-testid={`del-${p.id}`}><XCircle className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {(editing || creating) && (
        <MenuFormModal
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

function MenuFormModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || { name: "", price: 0, category: "Starters", description: "", image: "", variants: [], spicy: false, popular: false, available: true }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    setSaving(true);
    try {
      if (initial) await api.put(`/admin/menu/${initial.id}`, form);
      else await api.post(`/admin/menu`, form);
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="menu-form-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-black text-2xl mb-4">{initial ? "Edit item" : "Add new item"}</div>

        <div className="space-y-3">
          <Field label="Name"><input className="mj-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price (₹)"><input type="number" className="mj-input" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} data-testid="form-price" /></Field>
            <Field label="Category">
              <select className="mj-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
                <option>Starters</option><option>Mains</option><option>Favourites</option><option>Extra</option>
              </select>
            </Field>
          </div>

          {/* Image upload + URL */}
          <Field label="Image">
            <div className="space-y-2">
              {form.image && (
                <div className="relative group rounded-xl overflow-hidden border border-gray-200">
                  <img src={form.image} alt="preview" className="w-full h-40 object-cover" data-testid="form-image-preview" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image: "" })}
                    className="absolute top-2 right-2 rounded-full bg-black/70 text-white p-1.5 opacity-0 group-hover:opacity-100 transition"
                    data-testid="form-image-clear"
                    title="Remove image"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => uploadImage(e.target.files?.[0])}
                  className="hidden"
                  data-testid="form-image-file"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-300 hover:border-black hover:bg-gray-50 transition px-4 py-3 text-sm font-bold disabled:opacity-60"
                  data-testid="form-image-upload-btn"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading..." : form.image ? "Replace image" : "Upload from device"}
                </button>
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400 text-center">or paste a URL</div>
              <input
                className="mj-input"
                value={form.image && !form.image.startsWith("data:") ? form.image : ""}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                placeholder="https://..."
                data-testid="form-image-url"
              />
              <p className="text-[11px] text-gray-400">PNG / JPG / WEBP · max 3 MB</p>
            </div>
          </Field>

          <Field label="Description"><textarea rows={2} className="mj-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-desc" /></Field>

          <Field label="Variants (optional)">
            <div className="space-y-2">
              {(form.variants || []).map((v, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input placeholder="Label" className="mj-input flex-1" value={v.label} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) })} />
                  <input placeholder="+₹" type="number" className="mj-input w-24" value={v.delta} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, i) => i === idx ? { ...x, delta: Number(e.target.value) } : x) })} />
                  <button onClick={() => setForm({ ...form, variants: form.variants.filter((_, i) => i !== idx) })} className="text-[#C85A5A]"><XCircle className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={() => setForm({ ...form, variants: [...(form.variants || []), { label: "", delta: 0 }] })} className="text-xs font-bold inline-flex items-center gap-1 text-gray-600 hover:text-black">
                <Plus className="w-3 h-3" /> Add variant
              </button>
            </div>
          </Field>

          <div className="flex items-center gap-5 text-sm">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.spicy} onChange={(e) => setForm({ ...form, spicy: e.target.checked })} data-testid="form-spicy" /> Spicy</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} data-testid="form-popular" /> Popular</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} data-testid="form-available" /> Available</label>
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-bold text-gray-500" data-testid="form-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-black text-white rounded-full px-5 py-2 text-sm font-bold disabled:bg-gray-400" data-testid="form-save">
            {saving ? "Saving..." : (initial ? "Save changes" : "Add item")}
          </button>
        </div>
      </div>

      <style>{`.mj-input { width:100%; border:2px solid #E5E5E5; border-radius:12px; padding:10px 14px; font-size:14px; outline:none; }
      .mj-input:focus { border-color:#000; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

/* ----------------- USERS ----------------- */
function UsersPanel({ users, onRefresh }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // { mode: "one"|"bulk", ids: [] }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (needle) {
        const phones = (u.phones || []).join(" ");
        const hay = `${u.email || ""} ${u.name || ""} ${phones}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (filter === "returning") return u.is_returning;
      if (filter === "new") return u.is_first_time_customer;
      if (filter === "never") return u.never_ordered;
      return true;
    });
  }, [users, q, filter]);

  const allSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) { filtered.forEach((u) => next.delete(u.id)); }
    else { filtered.forEach((u) => next.add(u.id)); }
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const doDeleteOne = async (id) => {
    await api.delete(`/admin/users/${id}`);
    toast.success("User deleted");
    const s = new Set(selected); s.delete(id); setSelected(s);
    onRefresh();
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const { data } = await api.post(`/admin/users/bulk-delete`, { ids });
    toast.success(`${data.deleted} user${data.deleted === 1 ? "" : "s"} deleted`);
    setSelected(new Set());
    onRefresh();
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email or phone"
          className="flex-1 min-w-[220px] border-2 border-gray-200 focus:border-black rounded-full px-4 py-2 text-sm outline-none"
          data-testid="users-search"
        />
        {[
          { k: "all", l: "All" },
          { k: "returning", l: "Returning" },
          { k: "new", l: "First-time" },
          { k: "never", l: "Never ordered" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-bold border ${filter === f.k ? "bg-black text-white border-black" : "bg-white border-gray-200 text-gray-600"}`}
            data-testid={`users-filter-${f.k}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between bg-black text-white rounded-2xl px-5 py-3" data-testid="users-bulk-bar">
          <div className="text-sm font-bold">{selected.size} selected</div>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="rounded-full px-4 py-1.5 text-xs font-bold border border-white/30 hover:bg-white/10" data-testid="users-clear-selection">
              Clear
            </button>
            <button
              onClick={() => setConfirmDelete({ mode: "bulk", ids: Array.from(selected) })}
              className="rounded-full px-4 py-1.5 text-xs font-bold bg-[#C85A5A] hover:bg-[#b54848] inline-flex items-center gap-1.5"
              data-testid="users-bulk-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete selected
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto_auto] gap-4 px-5 py-3 border-b border-gray-100 text-[10px] uppercase tracking-widest font-bold text-gray-400 items-center">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-black" data-testid="users-select-all" />
          <div>Customer</div><div>Orders</div><div>Spent</div><div>Last order</div><div>Status</div><div></div>
        </div>
        {filtered.length === 0 && <div className="p-8 text-center text-sm text-gray-500">No users match.</div>}
        {filtered.map((u) => (
          <div key={u.id} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto_auto] gap-4 px-5 py-4 border-b border-gray-100 items-center" data-testid={`user-${u.id}`}>
            <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)} className="accent-black" data-testid={`user-check-${u.id}`} />
            <div className="min-w-0">
              <div className="font-bold text-sm truncate">{u.name || "(no name)"}</div>
              <div className="text-xs text-gray-500 truncate">{u.email}</div>
              {u.phones?.length > 0 && <div className="text-[11px] text-gray-500 mt-0.5">📞 {u.phones.join(", ")}</div>}
              <div className="text-[11px] text-gray-400 mt-0.5">Joined {fmt(u.signed_up_at)}</div>
            </div>
            <div className="text-right">
              <div className="font-display font-black">{u.orders}</div>
              {u.cancelled_orders > 0 && <div className="text-[10px] text-[#C85A5A]">{u.cancelled_orders} cancel</div>}
            </div>
            <div className="font-bold text-right whitespace-nowrap">₹{u.total_spent}</div>
            <div className="text-xs text-gray-600 text-right whitespace-nowrap">{fmt(u.last_order_at)}</div>
            <div>
              {u.is_returning && <span className="text-[10px] uppercase tracking-widest font-bold bg-black text-white rounded-full px-2 py-0.5">Returning</span>}
              {u.is_first_time_customer && <span className="text-[10px] uppercase tracking-widest font-bold bg-[#E8E2D2] text-black rounded-full px-2 py-0.5">First-time</span>}
              {u.never_ordered && <span className="text-[10px] uppercase tracking-widest font-bold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">No orders</span>}
            </div>
            <button
              onClick={() => setConfirmDelete({ mode: "one", ids: [u.id], name: u.name || u.email })}
              className="rounded-full p-2 hover:bg-red-50 text-[#C85A5A]"
              title="Delete this user"
              data-testid={`user-delete-${u.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {confirmDelete && (
        <ConfirmDeleteUsersModal
          count={confirmDelete.ids.length}
          name={confirmDelete.name}
          onClose={() => setConfirmDelete(null)}
          onConfirm={async () => {
            if (confirmDelete.mode === "one") await doDeleteOne(confirmDelete.ids[0]);
            else await doBulkDelete();
            setConfirmDelete(null);
          }}
        />
      )}
    </div>
  );
}

function ConfirmDeleteUsersModal({ count, name, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={onClose} data-testid="confirm-delete-users-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#C85A5A]/10 grid place-items-center">
            <AlertTriangle className="w-5 h-5 text-[#C85A5A]" />
          </div>
          <div className="font-display font-black text-xl">Delete {count > 1 ? `${count} users?` : "this user?"}</div>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          {count === 1 && name ? <><b>{name}</b> will be permanently removed.</> : <>The selected accounts will be permanently removed.</>}
          {" "}Their saved addresses will be wiped, and they <b>will need to register again</b> before they can log back in.
          Past orders stay in analytics but are detached from their account.
        </p>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-bold text-gray-500" data-testid="confirm-users-cancel">Cancel</button>
          <button
            onClick={go}
            disabled={busy}
            className="bg-[#C85A5A] text-white rounded-full px-5 py-2 text-sm font-bold disabled:bg-gray-300 inline-flex items-center gap-1.5"
            data-testid="confirm-users-delete"
          >
            <Trash2 className="w-4 h-4" /> {busy ? "Deleting..." : `Delete ${count > 1 ? "all " + count : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- ANALYTICS ----------------- */
function AnalyticsPanel({ analytics, onReset }) {
  const [showReset, setShowReset] = useState(false);
  if (!analytics) return <div className="text-gray-500">Loading…</div>;
  const maxRev = Math.max(1, ...analytics.daily_revenue.map((d) => d.revenue));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={IndianRupee} label="Today" value={`₹${analytics.today_revenue}`} sub={`${analytics.today_orders} orders`} />
        <StatCard icon={IndianRupee} label="This week" value={`₹${analytics.week_revenue}`} sub={`${analytics.week_orders} orders`} />
        <StatCard icon={IndianRupee} label="This month" value={`₹${analytics.month_revenue}`} sub={`${analytics.month_orders} orders`} />
        <StatCard icon={IndianRupee} label="Lifetime" value={`₹${analytics.total_revenue}`} sub={`${analytics.total_orders} orders`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total users" value={analytics.total_users} />
        <StatCard icon={Users} label="Returning (2+ orders)" value={analytics.returning_users ?? 0} />
        <StatCard icon={Users} label="First-time (1 order)" value={analytics.first_time_users ?? 0} />
        <StatCard icon={Users} label="Signed up · never ordered" value={analytics.never_ordered_users ?? 0} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Bell} label="Pending" value={analytics.pending_orders} accent={analytics.pending_orders > 0} />
        <StatCard icon={ShoppingBag} label="In transit" value={analytics.in_transit_orders} />
        <StatCard icon={CircleCheck} label="Completed" value={analytics.completed_orders} />
        <StatCard icon={XCircle} label="Cancelled" value={analytics.cancelled_orders} />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="font-display font-bold mb-4">Revenue · last 7 days</div>
        <div className="flex items-end gap-3 h-44">
          {analytics.daily_revenue.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="text-[9px] text-gray-400 font-bold">₹{d.revenue}</div>
              <div className="w-full bg-black rounded-t-md transition-all" style={{ height: `${(d.revenue / maxRev) * 100}%`, minHeight: d.revenue > 0 ? "4px" : "0" }} />
              <div className="text-[10px] text-gray-500">{d.day.slice(5)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="font-display font-bold mb-4">Top selling items</div>
        <div className="space-y-2">
          {analytics.top_items.map((i, idx) => (
            <div key={i.name} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-3"><span className="text-[10px] font-black text-gray-400 w-4">{idx + 1}</span>{i.name}</div>
              <div className="font-bold">{i.qty}</div>
            </div>
          ))}
          {analytics.top_items.length === 0 && <div className="text-sm text-gray-500">No data yet.</div>}
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-2xl border-2 border-[#C85A5A]/30 p-5" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#C85A5A]/10 grid place-items-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-[#C85A5A]" />
          </div>
          <div className="flex-1">
            <div className="font-display font-black text-lg">Danger zone</div>
            <p className="text-sm text-gray-600 mt-1">
              Permanently delete every order in the database. This wipes all analytics, revenue history, and order records. Customers and menu are kept. This cannot be undone.
            </p>
            <button
              onClick={() => setShowReset(true)}
              className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-[#C85A5A] text-[#C85A5A] hover:bg-[#C85A5A] hover:text-white transition px-4 py-2 text-sm font-bold"
              data-testid="reset-analytics-btn"
            >
              <Trash2 className="w-4 h-4" /> Reset all analytics & orders
            </button>
          </div>
        </div>
      </div>

      {showReset && <ResetAnalyticsModal onClose={() => setShowReset(false)} onConfirm={async () => { await onReset(); setShowReset(false); }} />}
    </div>
  );
}

function ResetAnalyticsModal({ onClose, onConfirm }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const match = phrase === "DELETE";

  const go = async () => {
    if (!match) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={onClose} data-testid="reset-analytics-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#C85A5A]/10 grid place-items-center">
            <AlertTriangle className="w-5 h-5 text-[#C85A5A]" />
          </div>
          <div className="font-display font-black text-xl">Reset all analytics?</div>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          This will permanently delete <b>every order</b> in the system. Revenue history, customer order history, and all analytics will be wiped. This action <b>cannot be undone</b>.
        </p>
        <p className="text-sm text-gray-600 mt-3">
          To confirm, type <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono font-bold">DELETE</code> below:
        </p>
        <input
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#C85A5A] font-mono"
          placeholder="DELETE"
          data-testid="reset-confirm-input"
          autoFocus
        />
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-bold text-gray-500" data-testid="reset-cancel">Cancel</button>
          <button
            onClick={go}
            disabled={!match || busy}
            className="bg-[#C85A5A] text-white rounded-full px-5 py-2 text-sm font-bold disabled:bg-gray-300 inline-flex items-center gap-1.5"
            data-testid="reset-confirm"
          >
            <Trash2 className="w-4 h-4" /> {busy ? "Wiping..." : "Yes, wipe everything"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  const tid = String(label || "").toLowerCase().replace(/\s+/g, "-");
  return (
    <div className={`bg-white rounded-2xl border p-4 ${accent ? "border-[#C85A5A]/40 ring-1 ring-[#C85A5A]/20" : "border-gray-100"}`} data-testid={`stat-${tid}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-display font-black text-2xl mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}
